import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, errOf } from '../../lib/api';

interface Totals { budget: number; actual: number; variance: number }
interface Report {
  event: { id: string; name: string; starts_at: string | null; venue: string } | null;
  budget: { expense: Totals; income: Totals; other: Totals; externalIncome: number; netBudget: number; netActual: number; count: number };
  sponsors: { total: number; byStage: Record<string, number>; pledged: number; paid: number; outstanding: number };
  seating: { totalSeats: number; totalCapacity: number; unassignedCount: number; checkedIn: number; invitedSeats: number; tables: unknown[] };
  guests: { total: number; checkedIn: number; seats: number };
}

const money = (n: number): string => `HK$${Number(n || 0).toLocaleString()}`;
const card = { background: 'var(--color-card)', boxShadow: 'var(--shadow-card)' } as const;
const STAGES = ['lead', 'contacted', 'pitched', 'pledged', 'confirmed', 'paid', 'declined'] as const;

/** 管理台「活動報告」—— 一頁過嘅活動總結（可列印／另存 PDF）＋ 可選 AI 敘述 */
export default function AdminReportPage() {
  const { t } = useTranslation();
  const [report, setReport] = useState<Report | null>(null);
  const [narrative, setNarrative] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(
    async (ai: boolean) => {
      setBusy(true);
      setErr('');
      const r = await apiGet<{ report: Report; narrative?: string }>(`/api/admin/report${ai ? '?ai=1' : ''}`);
      setBusy(false);
      if (!r.ok || !r.data) {
        setErr(errOf(r.data) || t('rp_failed'));
        return;
      }
      setReport(r.data.report);
      if (typeof r.data.narrative === 'string') setNarrative(r.data.narrative.trim());
    },
    [t],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  if (!report) {
    return (
      <div className="max-w-3xl">
        <h2 className="font-bold text-lg mb-2">{t('admin_report')}</h2>
        <p className="text-sm" style={{ color: err ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
          {err || '…'}
        </p>
      </div>
    );
  }

  const b = report.budget;
  const s = report.sponsors;
  const net = b.netActual;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="font-bold text-lg">{t('admin_report')}</h2>
        <span className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void load(true)}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold"
            style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)', border: 'none', cursor: 'pointer' }}
          >
            {t('rp_ai')}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-lg px-3 py-1.5 text-sm"
            style={{ border: '1px solid var(--color-divider)', background: 'var(--color-card)', color: 'var(--color-text)', cursor: 'pointer' }}
          >
            {t('rp_print')}
          </button>
        </span>
      </div>
      {err && <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>{err}</p>}

      <section className="rounded-xl p-3 mb-4" style={card}>
        <h3 className="font-bold mb-1">{report.event?.name ?? t('rp_no_event')}</h3>
        <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
          {report.event?.starts_at ? report.event.starts_at.slice(0, 16).replace('T', ' ') : t('rp_no_date')}
          {report.event?.venue ? ` ｜ ${report.event.venue}` : ''}
        </p>
      </section>

      {narrative && (
        <section className="rounded-xl p-3 mb-4" style={card}>
          <h3 className="font-bold mb-1">{t('rp_ai_narrative')}</h3>
          <p className="text-sm whitespace-pre-wrap">{narrative}</p>
        </section>
      )}

      <section className="rounded-xl p-3 mb-4" style={card}>
        <h3 className="font-bold mb-2">{t('rp_scale')}</h3>
        <p className="text-sm">
          {t('rp_guests', { n: report.guests.total, seats: report.guests.seats })} ｜ {t('rp_checked', { n: report.guests.checkedIn })}
          {report.seating.unassignedCount > 0 ? ` ｜ ${t('rp_unassigned', { n: report.seating.unassignedCount })}` : ''}
        </p>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          {t('rp_tables', { n: report.seating.tables.length, seats: report.seating.totalSeats, cap: report.seating.totalCapacity })}
        </p>
      </section>

      <section className="rounded-xl p-3 mb-4" style={card}>
        <h3 className="font-bold mb-2">{t('rp_sponsors')}</h3>
        <p className="text-sm">
          {t('rp_sponsor_totals', { n: s.total, pledged: money(s.pledged), paid: money(s.paid), outstanding: money(s.outstanding) })}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
          {STAGES.map((st) => `${t(`sp_stage_${st}`)} ${s.byStage[st] ?? 0}`).join(' ／ ')}
        </p>
      </section>

      <section className="rounded-xl p-3 mb-4" style={card}>
        <h3 className="font-bold mb-2">{t('bg_summary')}</h3>
        <table className="text-sm w-full">
          <tbody>
            {([
              ['bg_expense', b.expense],
              ['bg_income', b.income],
            ] as const).map(([k, v]) => (
              <tr key={k}>
                <td>{t(k)}</td>
                <td className="text-right">
                  {t('bg_budget')} {money(v.budget)}
                </td>
                <td className="text-right">
                  {t('bg_actual')} {money(v.actual)}
                </td>
                <td className="text-right" style={{ color: v.variance > 0 ? 'var(--color-danger)' : undefined }}>
                  {money(v.variance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-sm mt-2">
          {t('bg_sponsor_paid')}：<b>{money(b.externalIncome)}</b>
        </p>
        <p className="text-sm mt-1">
          {t('bg_net_actual')}：
          <b style={{ color: net < 0 ? 'var(--color-danger)' : 'var(--color-primary)' }}>
            {money(net)}（{net < 0 ? t('bg_deficit') : t('bg_surplus')}）
          </b>
        </p>
      </section>
    </div>
  );
}
